import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Twitter, Linkedin, Mail, Download } from 'lucide-react';

interface Heading {
  id: string;
  text: string;
  level: number;
}

interface TableOfContentsProps {
  content: string;
}

export function TableOfContents({ content }: TableOfContentsProps) {
  const { t } = useTranslation('blog');
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Extract headings from Markdown content
  useEffect(() => {
    // Extract h2 and h3 from Markdown (## and ###)
    const headingRegex = /^(#{2,3})\s+(.+)$/gm;
    const extractedHeadings: Heading[] = [];
    let match;
    let index = 0;

    while ((match = headingRegex.exec(content)) !== null) {
      const level = match[1].length; // 2 for ##, 3 for ###
      const text = match[2].trim();

      // Generate ID same way as ArticleContent does
      const id = text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '-');

      extractedHeadings.push({
        id: `heading-${index}-${id}`,
        text,
        level,
      });

      index++;
    }

    setHeadings(extractedHeadings);
  }, [content]);

  // Intersection Observer for active heading
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-100px 0px -80% 0px',
      }
    );

    const headingElements = document.querySelectorAll(
      '.article-content h2, .article-content h3'
    );
    headingElements.forEach((elem) => observerRef.current?.observe(elem));

    return () => observerRef.current?.disconnect();
  }, [headings]);

  const handleClick = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  const handleShare = (platform: string) => {
    const url = window.location.href;
    const title = document.title;

    switch (platform) {
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
          '_blank'
        );
        break;
      case 'linkedin':
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
          '_blank'
        );
        break;
      case 'email':
        window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}`;
        break;
    }
  };

  if (headings.length === 0) return null;

  return (
    <aside className="hidden lg:block">
      <div className="sticky top-24 max-h-[calc(100vh-120px)] overflow-y-auto">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
          {/* Table of Contents */}
          <h3 className="text-sm font-heading font-semibold text-gray-900 mb-3 uppercase tracking-wide">
            {t('toc.title')}
          </h3>

          <nav>
            <ul className="space-y-1">
              {headings.map((heading) => (
                <li
                  key={heading.id}
                  className={heading.level === 3 ? 'ml-4' : ''}
                >
                  <button
                    onClick={() => handleClick(heading.id)}
                    className={`text-left w-full text-sm py-2 px-2 rounded transition-all ${
                      activeId === heading.id
                        ? 'bg-aspexit-green text-white font-semibold'
                        : heading.level === 2
                        ? 'text-gray-900 hover:bg-gray-100 font-semibold'
                        : 'text-gray-600 hover:bg-gray-100 font-normal'
                    }`}
                  >
                    {heading.text}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* Divider */}
          <div className="my-4 border-t border-gray-200" />

          {/* Download PDF - More discrete */}
          <button className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors mb-3">
            <Download className="w-3.5 h-3.5" />
            {t('toc.downloadPdf')}
          </button>

          {/* Share Buttons - Smaller */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {t('toc.share')}
            </p>
            <div className="flex gap-1.5">
              <button
                onClick={() => handleShare('twitter')}
                className="flex-1 flex items-center justify-center p-2 bg-white border border-gray-200 rounded hover:bg-[#1DA1F2] hover:text-white hover:border-[#1DA1F2] transition-colors"
                aria-label={t('toc.shareTwitter')}
              >
                <Twitter className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => handleShare('linkedin')}
                className="flex-1 flex items-center justify-center p-2 bg-white border border-gray-200 rounded hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2] transition-colors"
                aria-label={t('toc.shareLinkedin')}
              >
                <Linkedin className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => handleShare('email')}
                className="flex-1 flex items-center justify-center p-2 bg-white border border-gray-200 rounded hover:bg-gray-600 hover:text-white hover:border-gray-600 transition-colors"
                aria-label={t('toc.shareEmail')}
              >
                <Mail className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
